import "../styles/globals.scss";
import { ReactElement, ReactNode, useEffect } from "react";
import type { AppProps } from "next/app";
import type { NextPage } from "next";
import dynamic from "next/dynamic";

import { theme } from "~/theme";
import { ThemeProvider } from "styled-components";

import { ConfigContextProvider } from "~/providers/ConfigContextProvider";
import { SettingsContextProvider } from "~/providers/SettingsContextProvider";

import { GlobalStyle } from "~/theme/globalstyle";

import { AccessibiliyHelpers } from "~/components/app/AccessibiliyHelpers";
import { HeaderContextProvider } from "~/providers/HeaderContextProvider";
import { MenuContextProvider } from "~/providers/MenuContextProvider";
import { TickerContextProvider } from "~/providers/TickerContextProvider";
import { PageStateContextProvider } from "~/providers/PageStateContextProvider";
import { withPasswordProtect } from "~/components/app/PasswordProtect";
import { AppDefaultHead } from "~/components/app/AppDefaultHead";
import { CssVarsContextProvider } from "~/providers/CssVarsContextProvider";

const SmoothScroll = dynamic(() => import("~/components/ui/SmoothScroll"));

type GetLayoutType = (page: ReactElement) => ReactNode;
type GetLayoutWithPropsType = (page: ReactElement, props: any) => ReactNode;

type NextPageWithLayout = NextPage & {
  getLayout?: GetLayoutType | GetLayoutWithPropsType;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

  return (
    <>
      {!("scrollBehavior" in document.documentElement.style) && (
        <SmoothScroll />
      )}
      <CssVarsContextProvider>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <AccessibiliyHelpers />
          <ConfigContextProvider>
            <SettingsContextProvider
              frontendSettings={pageProps.frontendSettings}
            >
              <TickerContextProvider>
                <MenuContextProvider>
                  <PageStateContextProvider>
                    <HeaderContextProvider>
                      <AppDefaultHead />
                      {getLayout(<Component {...pageProps} />, pageProps)}
                    </HeaderContextProvider>
                  </PageStateContextProvider>
                </MenuContextProvider>
              </TickerContextProvider>
            </SettingsContextProvider>
          </ConfigContextProvider>
        </ThemeProvider>
      </CssVarsContextProvider>
    </>
  );
}

export default `${process.env.NEXT_PUBLIC_PREVIEW_LOCKED}` === "1"
  ? withPasswordProtect(MyApp)
  : MyApp;
